import React, { useMemo, useState, useEffect } from 'react';

import { ApolloProvider } from '@apollo/react-common';
import { CacheLocation } from '@auth0/auth0-spa-js';
import { ThemeProvider } from '@material-ui/styles';
import { BrowserRouter, StaticRouter, useHistory } from 'react-router-dom';

import createApolloClient from '../src/graphql';
import { AUTH_APP_ID_KEY } from './const';
import { CommonLayout } from './containers/CommonLayout';
import { AuthProvider } from './context/AuthProvider';
import { AuthProviderOptions } from './context/AuthProvider/AuthProvider';
import { DiscoverController } from './context/DiscoverContext';
import { ModalsController } from './context/ModalsContext';
import { RXDController } from './context/RXDContext';
import { ScriptsController } from './context/ScriptsContext';
import { TestController } from './context/TestContext';
import theme from './theme';
import { SUSTAINABILITY_REFERENT } from './utils/const';
import { useAuth } from './utils/hooks';

declare global {
  interface Window {
    UE?: {
      pageHit?: (data: object) => void;
      resetAuth: (data: object) => void;
    };
    userengage?: (eventName: string, data: any) => Promise<any>;
    invitePageCustomTexting?: {
      [locale: string]: {
        title?: string;
        description?: string;
        signUpBtn?: string;
      };
    };
  }
}

const ROUTERS = {
  server: StaticRouter,
  client: BrowserRouter,
};

const getApolloClient = (type, client, redirect, getToken) => {
  if (type === 'server') {
    return client;
  }
  return createApolloClient(redirect, undefined, undefined, undefined, getToken);
};

function ApolloLoader({
  type = 'client',
  client,
  setAuthAppId,
  host,
  isAfterAuthorization,
  setIsAfterAuthorization,
  hasAuthenticatedCookie,
}) {
  const history = useHistory();
  const { isAuthenticated, getAccessTokenSilently, getAccessTokenWithPopup } = useAuth();

  const apolloClient = useMemo(() => {
    return getApolloClient(
      type,
      client,
      () => history.replace('/'),
      isAuthenticated
        ? async () => {
            try {
              return await getAccessTokenSilently();
            } catch (e) {
              try {
                return await getAccessTokenWithPopup();
              } catch (e) {}
            }
          }
        : null,
    );
  }, [getAccessTokenSilently, getAccessTokenWithPopup, client, history, type, isAuthenticated]);

  apolloClient.writeData({
    data: {
      hasNews: !host?.includes(SUSTAINABILITY_REFERENT),
    },
  });

  return (
    <ApolloProvider client={apolloClient}>
      <ThemeProvider theme={theme}>
        <CommonLayout
          setAuthAppId={setAuthAppId}
          isAfterAuthorization={isAfterAuthorization}
          setIsAfterAuthorization={setIsAfterAuthorization}
          hasAuthenticatedCookie={hasAuthenticatedCookie}
        />
      </ThemeProvider>
    </ApolloProvider>
  );
}

const authProps: AuthProviderOptions = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN || '',
  clientId: '',
  cacheLocation: 'localstorage' as CacheLocation,
  redirect_uri: window?.location?.origin,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE || '',
  scope: 'has-subscription',
  useRefreshTokens: true,
};

function App({ type, client = undefined, context, location, host = '', hasAuthenticatedCookie }) {
  const Router = ROUTERS[type];
  const [authAppId, setAuthAppId] = useState('');
  const [isAfterAuthorization, setIsAfterAuthorization] = useState(false);

  const loginCallback = () => {
    setIsAfterAuthorization(true);
  };

  useEffect(() => {
    const appId = localStorage.getItem(AUTH_APP_ID_KEY);
    if (appId) {
      setAuthAppId(appId);
    }
  }, []);

  return (
    <>
      <ModalsController>
        <ScriptsController>
          <RXDController>
            <DiscoverController>
              <TestController>
                <Router context={context} location={location}>
                  <AuthProvider {...authProps} clientId={authAppId} loginCallback={loginCallback}>
                    <ApolloLoader
                      type={type}
                      client={client}
                      setAuthAppId={setAuthAppId}
                      host={host}
                      isAfterAuthorization={isAfterAuthorization}
                      setIsAfterAuthorization={setIsAfterAuthorization}
                      hasAuthenticatedCookie={hasAuthenticatedCookie}
                    />
                  </AuthProvider>
                </Router>
              </TestController>
            </DiscoverController>
          </RXDController>
        </ScriptsController>
      </ModalsController>
    </>
  );
}

export default App;
